<template>
	<CallDraggable :data="call" :type="CallDragItemType.MyCallsCall" :draggable="true" :target="false">
		<div v-if="call" class="call-box" @contextmenu="showContextMenu" @dblclick="dblClick" @click="showContextMenuDelayed">
			<div class="shrink-0" v-if="picture">
				<div style="position:relative">
					<div class="state-icon2" :style="{'background':stateColor}">
						<GlyphIcon :icon="stateIcon" size="15" color="#fff" :class="{'onhold':call.state === PbxCallState.CALL_HOLD, 'ringing':call.state === PbxCallState.CALL_RING}" />
					</div>
					<EmployeePicture :picture="picture" :size="47" :name="call.leg === 'I' ? call.callee.name : call.caller.name" />
				</div>
			</div>

			<!-- state icon -->
			<div class="state-icon" :style="{'background':stateColor}" v-if="!picture">
				<GlyphIcon :icon="stateIcon" size="25" color="#fff" :class="{'onhold':call.state === PbxCallState.CALL_HOLD, 'ringing':call.state === PbxCallState.CALL_RING}" />
			</div>

			<!-- Name and number (and variable) -->
			<div class="labels">
				<div v-if="call.leg === 'I' && call.callee.name" class="name">{{ call.callee.name }}</div>
				<div v-if="call.leg === 'I' && !call.callee.name" class="name">{{ ss.cutNationalPrefix(call.callee.number) }}</div>
				<div v-if="call.leg === 'O' && !call.caller.secret && call.caller.name" class="name">{{ call.caller.name }}</div>
				<div v-if="call.leg === 'O' && !call.caller.secret && !call.caller.name" class="name">{{ ss.cutNationalPrefix(call.caller.number, true) }}</div>
				<div v-if="call.leg === 'O' && call.caller.secret" class="name">{{ t('mycalls.hiddenNumber') }}</div>
				<div class="number">
					<div class="flex items-center justify-start">
						<div v-if="call.leg === 'O' && call.caller.queueId"><glyph-icon :icon="GlyphIconName['user-group']" color="var(--label-queue-color)" size="13" /></div>
						<div v-if="call.leg === 'O' && call.caller.queueId" class="queue">{{ call.caller.queueName }}&nbsp;</div>
						<div v-if="call.leg === 'I' && call.callee.name">{{ ss.cutNationalPrefix(call.callee.number, true) }}</div>
						<div v-if="call.leg === 'O' && !call.caller.secret && call.caller.name">{{ ss.cutNationalPrefix(call.caller.number, true) }}</div>
						<CountryFlag v-if="countryFlagName" :icon="countryFlagName" size="13" class="country-flag" />
					</div>
					<div class="flex items-center">
						<div v-if="call.leg === 'O'" style="color:var(--label-otherparty-color);font-size:10px;margin-left:5px;margin-top:1px"><GlyphIcon :icon="GlyphIconName['chevron-right']" size="11" /></div>
						<div v-if="call.leg === 'O'" style="color:var(--label-otherparty-color);font-size:10px">{{ ss.cutNationalPrefix(call.callee.number, true) }}</div>
					</div>
				</div>
				<div v-if="variableValue" class="variable"><span>{{ variableName }}:</span> {{ variableValue }}</div>
			</div>

			<!-- Duration -->
			<div class="duration-box" :class="{ 'visible': call.state !== PbxCallState.CALL_RING }">
				{{ duration(currentCallDuration, true, true) }}
			</div>
		</div>
		<div v-if="!last" class="bottom-line"></div>
		<ContextMenu ref="contextMenu" :model="contextMenuItems" appendTo="body" popup @show="contextMenuVisible = true" @hide="contextMenuVisible = false" />
	</CallDraggable>
</template>

<script setup lang="ts">
import { useSettingsStore } from '@/stores/Settings'
import { useEmployeesStore } from '@/stores/Employees'
import { useRecordingStateStore } from '@/stores/RecordingState'
import { useApiStore } from '@/stores/Api'
import { useMyCallsStore } from '@/stores/MyCalls'
import { usePhonebookStore } from '@/stores/Phonebook'
import { useI18n } from 'vue-i18n'
import globalEvents from '@/utilities/GlobalEvents'
import DesktopNotification from '@/utilities/DesktopNotification'
import SipClient from '@/utilities/SipClient'
import logger from '@/utilities/Logger'
import GlyphIcon from '@/components/GlyphIcon.vue'
import CountryFlag from '@/components/CountryFlag.vue'
import CallDraggable from '@/components/dashboard/CallDraggable.vue'
import { type PbxAppEmployeesResponse, type PbxAppEmployeesExtensionCallWithDate } from '@/TcxApi/PbxApp'
import { computed, onUnmounted, ref, watch } from 'vue'
import { PbxCallState } from '@/TcxApi/PbxCommon'
import { EmployeeRecordingMode } from '@/TcxApi/Employee'
import ISO3166 from '@/utilities/iso3166'
import { GlyphIconName } from '@/stores/GlyphIcons.types'
import type { ErrorResponseDetail } from '@/TcxApi/ErrorResponse'
import type TieredMenu from 'primevue/tieredmenu'
import { SipClientTcxState, type SipClientRTCSession } from '@/utilities/SipClient.types'
import { CallDragItemType } from '@/utilities/CallDrag'
import { duration } from '@/utilities/filters'
import type { MenuItem } from 'primevue/menuitem'
import { DK_NUMBER_TYPE, dkNumberType } from '@/utilities/Tools'
import { PbxPhonebookContactPhoneNumberType, PbxPhonebookContactType, type PbxPhonebookContact } from '@/TcxApi/PbxPhonebook'

const ss = useSettingsStore()
const employees = useEmployeesStore()
const recordingState = useRecordingStateStore()
const api = useApiStore()
const myCalls = useMyCallsStore()
const phonebook = usePhonebookStore()
const { t } = useI18n()
const sipClient = SipClient.instance

interface Props {
	/** Index of this call in the list of calls */
	index: number
	/** Value of variable, if one should be display */
	variable?: string
	/** Label for variable, if used */
	variableName?: string
}

const props = defineProps<Props>()

const call = ref<PbxAppEmployeesExtensionCallWithDate>(employees.me!.calls[props.index] as PbxAppEmployeesExtensionCallWithDate) // eslint-disable-line
const last = computed(() => { return employees.me ? employees.me.calls.length - 1 === props.index : 0 })
const currentCallDuration = ref(0)
// const variableValue = ref<string | undefined>(undefined)
const timer2 = ref<ReturnType<typeof setInterval> | undefined>(undefined)
const autoAnswerPerformed = ref(false)
const contextMenu = ref<InstanceType<typeof TieredMenu>>()
const incomingCallPopupNotification = ref<DesktopNotification | undefined>(undefined)
const session = ref<SipClientRTCSession | undefined>(undefined)
const contextMenuTimeout = ref<ReturnType<typeof setTimeout> | undefined>(undefined)
const contextMenuVisible = ref(false)
const contextMenuClosedAt = ref<number>(0)
const picture = ref<string | undefined>(undefined)

const variableValue = computed(() => {
	if (!props.variable || !call.value) { return undefined }
	return employees.callVariables.get(`${call.value.callerChannelId}-${props.variable}`)
})

watch(contextMenuVisible, newValue => {
	if (!newValue) { contextMenuClosedAt.value = new Date().getTime() }
})

const callsICanTransferTo = computed(() => {
	if (!employees.me) { return [] }
	return employees.me.calls.filter(ec => ec._id != call.value._id && (ec.state === PbxCallState.CALL_ANSWER || ec.state === PbxCallState.CALL_HOLD))
})

const isRecording = computed(() => {
	return recordingState.isRecording && recordingState.recordingIds.includes(call.value.channel)
})

const canRecord = computed(() => {
	return ss.isAuthenticated && ss.auth?.recordingMode === EmployeeRecordingMode.DEMAND
})

const countryFlagName = computed(() => {
	if (!call.value || (call.value.leg === 'O' && call.value.caller.secret)) { return '' }
	let number = (call.value.leg === 'I' ? call.value.callee.number : call.value.caller.number).substring(0, 6)
	let iso: string | undefined = undefined

	while (!iso && number) {
		number = number.slice(0, -1)
		iso = ISO3166.prefix[number]
	}
	if (iso) { iso = iso.toLowerCase() }
	if (number === ss.pbxSettings.nationalPrefix) { iso = undefined }
	return iso
})

const stateIcon = computed(() => {
	let icon = ''

	if (call.value) {
		if (call.value.state === PbxCallState.CALL_HOLD) { icon = GlyphIconName['call-pause'] }
		else if (call.value.leg === 'I') {
			if (call.value.state === PbxCallState.CALL_START) { icon = GlyphIconName['call-dialing'] }
			else if (call.value.state === PbxCallState.CALL_ANSWER) { icon = GlyphIconName['call-outgoing'] }
		}
		else if (call.value.state === PbxCallState.CALL_START || call.value.state === PbxCallState.CALL_RING) { icon = GlyphIconName['call-ringing'] }
		else if (call.value.state === PbxCallState.CALL_ANSWER) { icon = GlyphIconName['call-incoming'] }
	}
	return icon as GlyphIconName
})

const stateColor = computed(() => {
	if (!call.value) { return '#fff' }
	switch (call.value.state) {
		case PbxCallState.CALL_ANSWER: return 'var(--call-answered)'
		case PbxCallState.CALL_HOLD: return 'var(--call-hold)'
		case PbxCallState.CALL_RING: return 'var(--call-ringing)'
		case PbxCallState.CALL_START:
			if (call.value.leg === 'I') {
				return 'var(--call-start)'
			} else {
				return 'var(--call-ringing)'
			}
		default:
			return 'var(--call-start)'
	}
})

const canSendSms = computed(() => {
	const number = call.value.leg === 'I' ? call.value.callee.number : call.value.caller.number

	return dkNumberType(number, true) === DK_NUMBER_TYPE.MOBILE
})

const contextMenuItems = computed(() => {
	if (!employees.me) { return [] }

	const transferMenu: MenuItem[] = []

	for (const other of callsICanTransferTo.value) {
		let label = ''

		if (other.leg === 'I') {
			label = other.callee.name || ss.cutNationalPrefix(other.callee.number) as string
		} else if (other.caller.secret) { label = t('mycalls.hiddenNumber') }
		else { label = other.caller.name || ss.cutNationalPrefix(other.caller.number) as string }

		transferMenu.push({ label, command: () => transferTo(other) })
	}

	const canHold = call.value.state === PbxCallState.CALL_ANSWER && (myCalls.phoneType == 'SIPPHONE' || myCalls.phoneType === 'SOFTPHONE') && call.value.iOwnHold === undefined
	const canUnHold = call.value.state === PbxCallState.CALL_HOLD && call.value.holdOwner && (myCalls.phoneType === 'SIPPHONE' || myCalls.phoneType === 'SOFTPHONE') && call.value.iOwnHold

	const items: MenuItem[] = [
		{ visible: call.value.state === PbxCallState.CALL_RING && (myCalls.phoneType === 'SIPPHONE' || myCalls.phoneType === 'SOFTPHONE'), icon: 'pi pi-phone', command: () => answer(), label: t('mycalls.action.answer') },
		{ visible: canHold, icon: 'pi pi-pause', command: () => hold(), label: t('mycalls.action.hold') },
		{ visible: canUnHold, icon: 'pi pi-phone', command: () => unhold(), label: t('mycalls.action.resume') },
		{ visible: call.value.state === PbxCallState.CALL_RING, command: () => hangup(), icon: 'pi pi-ban', label: t('mycalls.action.reject') },
		{ visible: call.value.state !== PbxCallState.CALL_RING, command: () => hangup(), icon: 'pi pi-phone rotate-135', label: t('mycalls.action.hangup') },
		{ visible: transferMenu.length > 0, label: t('mycalls.action.transferTo'), icon: 'pi pi-reply', items: transferMenu },
		{ separator: true },
		{ visible: canRecord.value && call.value.state === PbxCallState.CALL_ANSWER, command: () => recordingToggle(), icon: 'pi pi-circle-fill', label: isRecording.value ? t('mycalls.action.stopRecording') : t('mycalls.action.startRecording') },
		{ label: t('mycalls.action.sendSms'), icon: 'pi pi-comment', command: () => sendSms(), visible: () => canSendSms.value },
		{ visible: !!variableValue.value, command: () => copyVariableToClipboard(), icon: 'pi pi-copy', label: t('mycalls.action.copyVariableToClipboard').replace('%%name%%', (props.variableName ?? '').toLowerCase()) },
		{ visible: call.value.leg === 'I', command: () => copyNumberToClickboard(call.value.callee.number), icon: 'pi pi-copy', label: t('mycalls.action.copyNumberToClipboard') },
		{ visible: call.value.leg === 'O' && !call.value.caller.secret, command: () => copyNumberToClickboard(call.value.caller.number), icon: 'pi pi-copy', label: t('mycalls.action.copyNumberToClipboard') },
		{ label: t('mycalls.action.createContact'), icon: 'pi pi-user-plus', command: () => createContactFromCall(call.value) },
		{ label: t('mycalls.action.cdrNote'), icon: 'pi pi-pen-to-square', command: () => globalEvents.emit('CdrNoteEditor', call.value) }
	]

	return items
})

watch(() => call.value.state, (newValue: PbxCallState, oldValue: PbxCallState) => {
	if (incomingCallPopupNotification.value && newValue !== PbxCallState.CALL_START && newValue !== PbxCallState.CALL_RING) {
		incomingCallPopupNotification.value.close()
	}

	// Ensure that if user calls a *7x number, and the call is answered, then the caller ID is updated with the one who the *7x connected the user to
	if (call.value.leg === 'I' && call.value.callee.number.match(/^\*7[123]/) && newValue === PbxCallState.CALL_ANSWER && oldValue !== PbxCallState.CALL_HOLD) {
		setTimeout(async () => {
			const res = await api.get<PbxAppEmployeesResponse>(`/pbx/app/employees?calls=true&ids=${ss.auth?.employee}`)

			if (res.data[0]) {
				for (const ext of res.data[0].extensions) {
					const extensionCall = ext.calls.find(ec => ec._id === call.value._id)
					if (extensionCall) {
						call.value.callee.number = extensionCall.callee.number
						call.value.callee.name = extensionCall.callee.name
						call.value.callerChannelId = extensionCall.callerChannelId
					}
				}
			}

			// getAndShowVariable()
		}, 500)
	}
})

function createContactFromCall(call: PbxAppEmployeesExtensionCallWithDate) {
	const item = call.leg === 'I' ? call.callee : call.caller

	if (item.number) {
		const contact: PbxPhonebookContact = {
			_id: '',
			customer: ss.auth?.customer as string,
			type: PbxPhonebookContactType.PERSON,
			addresses: [],
			phoneNumbers: [{ type: PbxPhonebookContactPhoneNumberType.MAIN, number: item.number }],
			emailAddresses: [],
			notes: '',
			employee: ss.auth?.employee as string,
			givenName: null,
			familyName: null,
			company: null,
			title: null,
			department: null,
			shortNumber: null,
			picture: null,
			externalId: null,
			name: null
		}
		if (item.name && item.name.includes(' ')) {
			contact.givenName = item.name.substring(0, item.name.lastIndexOf(' '))
			contact.familyName = item.name.substring(item.name.lastIndexOf(' ') + 1)
		} else if (item.name) {
			contact.givenName = item.name
		}

		globalEvents.emit('Phonebook:CreateNew', contact)
	}
}

function sendSms() {
	const number = call.value.leg === 'I' ? call.value.callee.number : call.value.caller.number

	globalEvents.emit('CommonSendSmsModal', { employee: undefined, number: ss.cutNationalPrefix(number) })
}

async function transferTo(other: PbxAppEmployeesExtensionCallWithDate) {
	// Attended transfer
	// console.log('Omstil aktivt kald med ' + (call.value.leg === 'I' ? call.value.callee.number : call.value.caller.number) + ' til ' + (other.leg == 'I' ? other.callee.number : other.caller.number)) // eslint-disable-line
	let callOriginator = call.value
	let callReceiver = other

	// We swap the callOriginator and callReceiver, else the PBX won't transfer the call.
	if (callOriginator.state === PbxCallState.CALL_ANSWER && callReceiver.state === PbxCallState.CALL_HOLD) {
		callReceiver = call.value
		callOriginator = other
	}

	globalEvents.emit('AttendedTransferRequested', { source: callOriginator._id, destination: callReceiver._id })

	try { api.get(`/pbx/app/action/transfer?source=${callOriginator._id}&destination=${callReceiver._id}`) }
	catch (_) { globalEvents.emit('CommonErrorModal', { header: t('common.error.transferFailedHeader'), message: t('common.error.transferFailedMessage') }) }
}

/**
 * Transfer initiated from main MyCalls bottom buttons
 * @param data Transfer data, originator call and receiver call
 */
async function transferTo2(data: { originator: PbxAppEmployeesExtensionCallWithDate, receiver: PbxAppEmployeesExtensionCallWithDate }) {
	logger.info('TransferTo2 invoked')
	if (data.originator._id === call.value._id) {
		logger.info('TransferTo2 invoked - and its for me!')
		transferTo(data.receiver)
	}
}

function dblClick() {
	if (contextMenuTimeout.value) {
		clearTimeout(contextMenuTimeout.value)
		contextMenuTimeout.value = undefined
	}

	// If call is on hold and is SIP based...
	if (call.value.state === PbxCallState.CALL_HOLD && ['SIPPHONE', 'SOFTPHONE'].includes(myCalls.phoneType)) {
		if (call.value.holdOwner) {
			unhold()
		} else {
			hangup()
		}
	}

	// If call is an incoming call and is SIP based - then answer it
	else if (call.value.state === PbxCallState.CALL_RING && ['SIPPHONE', 'SOFTPHONE'].includes(myCalls.phoneType)) {
		answer()
	}

	// If I am calling out (is ringing) but have given up and want to end the call
	else if (call.value.state === PbxCallState.CALL_START && ['SIPPHONE', 'SOFTPHONE'].includes(myCalls.phoneType)) {
		hangup()
	}

	// I have finished talking and want to end the call - but not within the first 3 seconds
	else if (call.value.state === PbxCallState.CALL_ANSWER && call.value.answered && (new Date().getTime() - call.value.answered.getTime()) > 3000) {
		hangup()
	}
}

async function recordingToggle() {
	if (!employees.me) {
		globalEvents.emit('CommonErrorModal', { header: t('common.error.noUserProfileHeader'), message: t('common.error.noUserProfileMessage') })
		return
	}

	try { await api.get('/pbx/recording/ondemand?extension=' + employees.me.primaryExtension?._id + '&mode=TOGGLE') }
	catch (err) {
		if ((err as ErrorResponseDetail).status === 403) {
			globalEvents.emit('CommonErrorModal', { header: t('mycalls.recordingHeader'), message: t('mycalls.recordingNotAllowed') })
		} else if ((err as ErrorResponseDetail).status === 404) {
			globalEvents.emit('CommonErrorModal', { header: t('mycalls.recordingHeader'), message: t('mycalls.recordingNoCall') })
		}
	}
}

function showContextMenu(event: MouseEvent) {
	event.preventDefault()
	if (contextMenuVisible.value) {
		contextMenu.value?.hide()
	} else {
		globalEvents.emit('HideContextMenu')
		contextMenu.value?.show(event)
	}
}

function showContextMenuDelayed(event: MouseEvent) {
	event.preventDefault()
	if (new Date().getTime() - contextMenuClosedAt.value < 100) {
		return
	}
	contextMenuTimeout.value = setTimeout(() => {
		if (contextMenuTimeout.value) {
			contextMenuTimeout.value = undefined
			if (!contextMenuVisible.value) {
				globalEvents.emit('HideContextMenu')
				contextMenu.value?.show(event)
			}
		}
	}, 200)
}

function hideContextMenu() {
	contextMenu.value?.hide()
}

function copyVariableToClipboard() {
	if (variableValue.value !== undefined) {
		navigator.clipboard.writeText(variableValue.value)
	}
}

function copyNumberToClickboard(number: string) {
	navigator.clipboard.writeText(number.replace(ss.pbxSettings.nationalPrefix, ''))
}

function refreshDuration() {
	if (call.value) {
		// If another call was autoanswered within the last 2 seconds, and this is a call from a queue - hang it up
		// (done to handle the pick first in queue and second in queue arrives before pickup is completed)
		if (call.value.state === PbxCallState.CALL_RING && myCalls.autoAnswer?.last && (new Date().getTime() - myCalls.autoAnswer.last.getTime()) < 2000 && !autoAnswerPerformed.value) {
			if (call.value.caller.queueId) {
				hangup()
			}
		}

		if (call.value.leg === 'I' && call.value.started) {
			currentCallDuration.value = Math.round((new Date().getTime() - call.value.started.getTime()) / 1000)
		} else if (call.value.answered) {
			currentCallDuration.value = Math.round((new Date().getTime() - call.value.answered.getTime()) / 1000)
		}
	} else {
		currentCallDuration.value = 0
	}
}

function autoAnswerCheck() {
	// console.log(`AutoAnswerCheck: I am ${call.value.sessionId} and autoAnswer says to answer ${myCalls.autoAnswer.sessionId}`) // eslint-disable-line
	// console.dir(call.value)
	if (call.value.state === PbxCallState.CALL_RING && myCalls.autoAnswer.enabled && (call.value.sessionId === myCalls.autoAnswer.sessionId || `${call.value.pbx}:${call.value.callerChannelId}` === myCalls.autoAnswer.sessionId)) {
		myCalls.callWasAutoAnswered()
		if (timer2.value) {
			clearInterval(timer2.value)
			timer2.value = undefined
		}
		autoAnswerPerformed.value = true
		answer()
	}
}

async function answer() {
	if (!(call.value.leg === 'O' && (call.value.state === PbxCallState.CALL_RING || call.value.state === PbxCallState.CALL_START))) {
		return // Cannot answer call that is not in ringing state
	}
	if (!ss.realPhoneId) { noPhoneSelected(); return }

	if (ss.settings.softphoneId) {
		const session = sipClient.getSession(call.value.callId, call.value.caller.number, call.value.state)
		if (session) {
			// Tell all other calls that are not ringing and not on hold to go on hold
			const sessionsNotOnHoldOrRinging = sipClient.sessions.filter(ses => {
				if (session === ses) { return false }
				if ([SipClientTcxState.CALL_HOLD, SipClientTcxState.CALL_RING].includes(ses.data.tcxState as SipClientTcxState)) { return false }
				return true
			})

			for (const session of sessionsNotOnHoldOrRinging) {
				globalEvents.emit('Call:ActivateHold', { sessionId: session.id })
			}

			sipClient.answer(session)
		}
	} else {
		try { api.get(`/pbx/app/action/answer?device=${ss.realPhoneId}&target=${call.value._id}`) }
		catch (_) { globalEvents.emit('CommonErrorModal', { header: t('mycalls.header'), message: t('mycalls.failedToAnswerCall') }) }
	}
}

async function hold() {
	if (!ss.realPhoneId) { return noPhoneSelected() }

	if (ss.settings.softphoneId) {
		const session = sipClient.getSession(call.value.callId, call.value.caller.number, call.value.state)
		if (session) {
			sipClient.hold(session)
		}
	} else {
		try { api.get(`/pbx/app/action/hold?device=${ss.realPhoneId}&target=${call.value._id}`) }
		catch (_) { globalEvents.emit('CommonErrorModal', { header: t('mycalls.header'), message: t('mycalls.failedToHoldCall') }) }
	}
}

async function unhold() {
	if (!ss.realPhoneId) { return noPhoneSelected() }

	if (ss.settings.softphoneId) {
		const session = sipClient.getSession(call.value.callId, call.value.caller.number, call.value.state)
		if (session) {
			// Tell all other call, not on hold and not ringing to go on hold
			const sessionsNotOnHoldOrRinging = sipClient.sessions.filter(ses => ses != session && ![SipClientTcxState.CALL_HOLD, SipClientTcxState.CALL_RING].includes(ses.data.tcxState as SipClientTcxState))

			for (const session of sessionsNotOnHoldOrRinging) {
				globalEvents.emit('Call:ActivateHold', { sessionId: session.id })
			}
			sipClient.unhold(session)
		}
		// session.data.putOnHoldAt = null
	} else {
		try { api.get(`/pbx/app/action/unhold?device=${ss.realPhoneId}&target=${call.value._id}`) }
		catch (_) { globalEvents.emit('CommonErrorModal', { header: t('mycalls.header'), message: t('mycalls.failedToResumeCall') }) }
	}
	// this.call.putOnHoldAt = null
}

async function hangup() {
	if (!ss.realPhoneId) { return noPhoneSelected() }

	if (ss.settings.softphoneId) {
		const session = sipClient.getSession(call.value.callId, call.value.caller.number, call.value.state)
		if (session) {
			if (call.value.state === PbxCallState.CALL_RING || call.value.state === PbxCallState.CALL_START) {
				sipClient.decline(session)
				globalEvents.emit('CallRejected', { source: call.value._id })
			} else {
				sipClient.hangup(session)
			}
		}
	} else {
		if (call.value.state === PbxCallState.CALL_RING) {
			globalEvents.emit('CallRejected', { source: call.value._id })
		}

		try { api.get(`/pbx/app/action/hangup?target=${call.value._id}`) }
		catch (_) {	globalEvents.emit('CommonErrorModal', { header: t('mycalls.header'), message: t('mycalls.failedToHangupCall') }) }
	}
}

function getPhoneType() {
	if (ss.settings.softphoneId) { return 'SOFTPHONE' }

	if (employees.me) {
		for (const ext of employees.me.extensions) {
			if (ext.phones) {
				for (const phone of ext.phones) {
					if (phone._id === ss.realPhoneId) { return phone.type }
				}
			}
		}
	}

	return ''
}

async function incomingCallPopup() {
	try {
		const myPhoneType = getPhoneType()
		const employee = await employees.getEmployeeByExtensionNumber(call.value.caller.number)

		let title = employee?.name
		if (!title) {
			if (call.value.caller.name) {
				title = call.value.caller.name + ' (' + call.value.caller.number.replace(ss.pbxSettings.nationalPrefix, '') + ')'
			} else {
				title = call.value.caller.number.replace(ss.pbxSettings.nationalPrefix, '')
			}
		}

		const body = (myPhoneType === 'SIPPHONE' || myPhoneType === 'SOFTPHONE') ? t('mycalls.clickToAnswer') : undefined

		if (call.value.caller.secret) { title = t('mycalls.hiddenNumber') }

		const icon = employee && employee.picture ? `https://image.telecomx.dk/${employee.picture}.png?width=256&height=256&token=${ss.token}` : undefined
		const onClick = myPhoneType === 'SIPPHONE' || myPhoneType === 'SOFTPHONE' ? () => {
			incomingCallPopupNotification.value = undefined
			answer()
		} : undefined

		const onClose = () => { incomingCallPopupNotification.value = undefined }

		// Show popup
		incomingCallPopupNotification.value = DesktopNotification.show(title, body, icon, true, onClick, onClose)
	}
	catch (err) {
		globalEvents.emit('CommonErrorModal', { header: t('common.genericErrorHeader'), message: (err as Error).message })
	}
}

function noPhoneSelected() {
	globalEvents.emit('CommonErrorModal', { header: t('common.error.noPhoneHeader'), message: t('common.error.noPhoneMessage') })
}

// async function getAndShowVariable() {
// 	if (props.variable && call.value.callerChannelId) {
// 		const value = employees.callVariables.get(`${call.value.callerChannelId}-${props.variable}`)
// 		if (value) { variableValue.value = value }
// 	}
// }

function sendDtmfEvent(key: string) {
	if (call.value.state === PbxCallState.CALL_ANSWER) {
		const session = sipClient.getSession(call.value.callId, call.value.caller.number, call.value.state)
		if (session) {
			sipClient.sendDTMF(key, session)
			sipClient.playDtmfTone(key)
		}
	}
}

/** This function is used for eventHandlers that are called through emitting GlobalEvents events from sipClient
 *  The SIP client emits events based on sessionId, so each event handler here has to match their own call with the corresponding session.
 */
function shouldRunEventFromSipClient(event?: { sessionId?: string, exceptCall?: string }) {
	if (!ss.settings.softphoneId) { return false }

	const session = sipClient.getSession(call.value.callId, call.value.caller.number, call.value.state)

	if (!event || (event.sessionId && event.sessionId === session?.id)) {
		return true
	}
	return false
}

function activateHoldEvent(event?: { sessionId?: string, exceptCall?: string }) {
	console.log(`ActivateHoldEvent: I am ${call.value.sessionId} and event is ${event?.sessionId}`) // eslint-disable-line
	if (!event || (event.exceptCall && call.value._id !== event.exceptCall) || event.sessionId === call.value.sessionId || shouldRunEventFromSipClient(event)) {
		if (call.value.state === PbxCallState.CALL_RING) {
			hangup()
		} else {
			hold()
		}
	}
}

function deactivateHoldEvent(event: { sessionId: string }) {
	// The emitter does not know the call._id of this call, but only the session
	if (shouldRunEventFromSipClient(event)) {
		answer()
		unhold()
	} else if (call.value.sessionId === event.sessionId && call.value.state === PbxCallState.CALL_HOLD) {
		unhold()
	}
}

function deactivateHoldInternal(event: { sessionId: string }) {
	if (shouldRunEventFromSipClient(event)) {
		unhold()
	}
}

function answerEvent(event: { sessionId: string }) {
	// The emitter does not know the call._id of this call, but only the session
	if (shouldRunEventFromSipClient(event)) {
		answer()
	}
}

function answerByNumberEvent(event: { callerNumber: string }) {
	if (call.value.caller.number === event.callerNumber && call.value.state === PbxCallState.CALL_RING) {
		answer()
	}
}

function hangupEvent(event: { sessionId: string }) {
	// The emitter does not know the call._id of this call, but only the session
	if (shouldRunEventFromSipClient(event)) {
		hangup()
	}
}

function rejectEvent(event: { sessionId: string }) { // NOSONAR
	// The emitter does not know the call._id of this call, but only the session
	if (shouldRunEventFromSipClient(event)) {
		hangup()
	}
}

globalEvents.on('OneSecond', refreshDuration)

if (ss.settings.softphoneId) {
	session.value = sipClient.getSession(call.value.callId, call.value.caller.number, call.value.state === PbxCallState.CALL_START ? PbxCallState.CALL_RING : call.value.state)
	globalEvents.on('Call:ActivateHold', activateHoldEvent)
	globalEvents.on('MyCallsSendDTMF', sendDtmfEvent)
	globalEvents.on('Call:DeactivateHold', deactivateHoldEvent)
	globalEvents.on('Call:DeactivateHoldInternal', deactivateHoldInternal)
	globalEvents.on('Call:Answer', answerEvent)
	globalEvents.on('Call:AnswerByCallerNumber', answerByNumberEvent)
	globalEvents.on('Call:Hangup', hangupEvent)
	globalEvents.on('Call:Reject', rejectEvent)
	globalEvents.on('HideContextMenu', hideContextMenu)
	globalEvents.on('Call:Transfer', transferTo2)

	// If I am making a new call, then all other calls go on hold
	if (call.value.leg === 'I' && call.value.state === PbxCallState.CALL_START) {
		globalEvents.emit('Call:ActivateHold', { exceptCall: call.value._id })
	}
} else if (ss.settings.phoneId) {
	globalEvents.on('Call:ActivateHold', activateHoldEvent)
	globalEvents.on('Call:DeactivateHold', deactivateHoldEvent)
	globalEvents.on('HideContextMenu', hideContextMenu)
	globalEvents.on('Call:Transfer', transferTo2)
}

if (myCalls.autoAnswer.enabled) {
	logger.info('MyCallsCall: Auto-answer enabled - starting timer')
	timer2.value = setInterval(() => { autoAnswerCheck() }, 50)
}

setTimeout(() => { refreshDuration() }, 100) // because it can take up to 1 seconds before the global event will do it

if (!ss.runtime.visible && call.value.leg === 'O' && (call.value.state === PbxCallState.CALL_START || call.value.state === PbxCallState.CALL_RING)) {
	incomingCallPopup()
}

if (call.value.leg === 'I') {
	// It is a call i am making
	const employee = employees.findEmployeeFromNumber(call.value.callee.number)
	if (employee) {
		call.value.callee.name = employee.name
		if (employee.picture) {
			picture.value = employee.picture
		}
	} else {
		const name = phonebook.findNameFromNumber(call.value.callee.number)

		if (name) { call.value.caller.name = name }

		const pbPicture = phonebook.findPictureFromNumber(call.value.callee.number)

		if (pbPicture) {
			picture.value = pbPicture
		}
	}
}

if (call.value.leg === 'O') {
	// It is a call i am receiving
	const name = phonebook.findNameFromNumber(call.value.caller.number)

	if (name) { call.value.caller.name = name }

	const pbPicture = phonebook.findPictureFromNumber(call.value.caller.number)

	if (pbPicture) {
		picture.value = pbPicture
	} else {
		const employee = employees.findEmployeeFromNumber(call.value.caller.number)
		if (employee?.picture) {
			picture.value = employee.picture
		}
	}

	// If user has been logged in for less than 20 seconds and queue used call variable - then we need to check with the server if the variable has a value and feed it to employee variables
	if (ss.authenticatedSeconds >= 0 && ss.authenticatedSeconds < 20 &&
		props.variable && employees.callVariables.get(`${call.value._id.split(':')[1]}-${props.variable}`) === undefined) {
		api.get<{ name: string, value: string }>(`/pbx/callvariable/${call.value._id}/${props.variable}`)
			.then(res => {
				if (res.data.value) {
					employees.callVariables.set(`${call.value._id.split(':')[1]}-${props.variable}`, res.data.value)
				}
			})
			.catch(_ => { /** ignore */ })
	}
}

// getAndShowVariable()

onUnmounted(() => {
	globalEvents.off('OneSecond', refreshDuration)
	if (ss.settings.softphoneId) {
		globalEvents.off('Call:ActivateHold', activateHoldEvent)
		globalEvents.off('MyCallsSendDTMF', sendDtmfEvent)
		globalEvents.off('Call:DeactivateHold', deactivateHoldEvent)
		globalEvents.off('Call:DeactivateHoldInternal', deactivateHoldInternal)
		globalEvents.off('Call:Answer', answerEvent)
		globalEvents.off('Call:Hangup', hangupEvent)
		globalEvents.off('Call:Reject', rejectEvent)
		globalEvents.off('HideContextMenu', hideContextMenu)
		globalEvents.off('Call:Transfer', transferTo2)
	} else if (ss.settings.phoneId) {
		globalEvents.off('Call:ActivateHold', activateHoldEvent)
		globalEvents.off('Call:DeactivateHold', deactivateHoldEvent)
		globalEvents.off('HideContextMenu', hideContextMenu)
		globalEvents.off('Call:Transfer', transferTo2)
	}
	if (timer2.value) { clearInterval(timer2.value) }
	if (incomingCallPopupNotification.value) { incomingCallPopupNotification.value.close() }
})
</script>

<style scoped>
	.call-box {
		display: flex;
		justify-content: space-between;
		flex-direction: row;
		flex-wrap: nowrap;
		align-items: center;
		transition: 400ms;
		transition-delay: 1s;
		padding-left: 5px;
		padding-right: 5px;
		height: 50px;
		column-gap: 5px;
		overflow: hidden;
		cursor: context-menu;

	}
	.state-icon {
		align-self: center;
		border-radius: 20px;
		width: 40px;
		height: 40px;
		flex-shrink: 0;
		display: flex;
		justify-content: center;
		align-items: center;
	}
	.state-icon > DIV {
		margin-top: -3px;
	}
	.state-icon2 {
		align-self: center;
		border-radius: 10px;
		width: 20px;
		height: 20px;
		flex-shrink: 0;
		display: flex;
		justify-content: center;
		align-items: center;
		position: absolute;
		right: -8px;
		bottom: 0px;
		z-index: 1;
	}
	.state-icon2 > DIV {
		margin-top: -1px;
	}
	.bottom-line {
		margin-left: 52px;
		border-bottom: 1px solid var(--p-content-border-color);
	}
	.labels {
		align-self: center;
		display: flex;
		flex-direction: column;
		justify-content: center;
		min-width: 0;
		flex-grow: 5;
		margin-left: 7px;
	}
	.labels > DIV {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.name {
		/* font-size: var(--font-size-primary); */
		font-weight: 500;
		line-height: 13px;
		color: var(--p-text-color);
		letter-spacing: 0.01em;
		min-width: 0;
	}
	.no-name {
		font-size: var(--font-size-primary);
		font-weight: 500;
		line-height: 13px;
		color: var(--p-text-color);
		letter-spacing: 0.01em;
	}
	.number {
		font-size: 13px;
		color: var(--p-text-muted-color);
		line-height: 13px;
		display: flex;
		align-items: center;
		justify-content: space-between;
		letter-spacing: 0.01em;
	}
	.country-flag {
		margin-left: 3px;
		margin-top: 2px;
	}
	.queue {
		color: var(--label-queue-color);
		font-weight: 500;
	}
	.variable {
		font-size: 11px;
		font-style: italic;
		color: var(--label-location-color);
		line-height: 11px;
		display: inline-flex;
		transition: 400ms;
	}
	.variable > SPAN {
		font-weight: 600;
	}
	.duration-box {
		flex-shrink: 0;
		display: flex;
		flex-direction: column;
		justify-content: center;
		transition: 1000ms;
		transform: translateX(65px);
		color: var(--p-text-muted-color);
		font-family: RobotoMonoThin;
		font-size: 12px;
		text-align: right;
		margin-left: 5px;
	}
	.duration-box.visible {
		transform: none;
	}
	.ringing {
		animation: shake 1s;
		animation-iteration-count: infinite;
	}

	@keyframes shake {
		0% { transform: translate(2px, 0px); }
		10% { transform: translate(-2px, 1px); }
		20% { transform: translate(2px, -1px); }
		30% { transform: translate(-2px, 0px); }
		40% { transform: translate(2px, 0px); }
		50% { transform: translate(-2px, 0px); }
		60% { transform: translate(2px, 0px); }
		70% { transform: translate(-2px, 1px); }
		80% { transform: translate(2px, -1px); }
		90% { transform: translate(-2px, 0px); }
		100% { transform: translate(2px, 0px); }
	}
	.onhold {
		animation: holdanim 1s;
		animation-iteration-count: infinite;
		margin-left: -5px;
		margin-top: 3px !important;
	}
	@keyframes holdanim {
		0% { opacity: 1; }
		40% { opacity: 0.2; }
		70% { opacity: 1; }
	}
</style>
